/* ============================================================================
 * portalPhotoMigration.js
 *
 * One-time utility that scans the contractor's existing portal photo
 * collections and re-hosts any legacy `data:image/...` entries into
 * Supabase Storage (bucket: `portal-photos`).
 *
 * The migration is *idempotent* and *silent*:
 *   - Already-migrated entries (Storage URLs) are skipped.
 *   - On success, the entry's `url` is replaced with the new public URL and
 *     a `migrated_at` timestamp is added.
 *   - Failures are tolerated (logged to console.warn) and re-tried on the
 *     next dashboard mount.
 *
 * Settings keys touched:
 *   - client_portal_shared_photos  → { [clientId]: [photo, ...] }
 *   - crew_portal_photos           → { [crewId]:   [photo, ...] }
 *
 * Run-control:
 *   We persist a small flag in localStorage so the migration is attempted
 *   at most once per page-load. (No server flag — re-running is harmless.)
 * ========================================================================== */

import { fetchSettingsByKey, upsertSettingsByKey } from '@/lib/settingsData';
import { uploadPortalPhoto } from '@/lib/portalStorage';

const CLIENT_KEY = 'client_portal_shared_photos';
const CREW_KEY = 'crew_portal_photos';
const SESSION_FLAG = 'kocore.portalMigration.attempted';

const isDataUrl = (s) => typeof s === 'string' && s.startsWith('data:');
const isStorageUrl = (s) =>
  typeof s === 'string' && s.includes('/storage/v1/object/public/portal-photos/');

/* Best-effort conversion: data URL → Blob → File-like object */
const dataUrlToBlob = (dataUrl) => {
  try {
    const [meta, b64] = dataUrl.split(',');
    if (!meta || !b64) return null;
    const mime = (meta.match(/data:([^;]+);base64/i) || [])[1] || 'image/jpeg';
    const binary = atob(b64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
    return new Blob([bytes], { type: mime });
  } catch (e) {
    return null;
  }
};

const migrateOnePhoto = async ({ photo, scope, ownerId }) => {
  if (!photo?.url || !isDataUrl(photo.url)) return photo;
  const blob = dataUrlToBlob(photo.url);
  if (!blob) return photo;
  // Wrap the blob with a name so Storage gets a friendly extension.
  const ext = (blob.type.split('/')[1] || 'jpg').replace('jpeg', 'jpg');
  const file = new File([blob], `legacy-${photo.id || Date.now()}.${ext}`, {
    type: blob.type,
  });
  const result = await uploadPortalPhoto(file, { scope, ownerId });
  if (!result.ok || !result.url) return photo;
  return {
    ...photo,
    url: result.url,
    path: result.path,
    migrated_at: new Date().toISOString(),
    legacy: true,
  };
};

const migrateBucket = async ({ key, scope }) => {
  const setting = await fetchSettingsByKey(key, {});
  const map = setting.value || {};
  if (!map || typeof map !== 'object' || Array.isArray(map)) {
    return { scope, scanned: 0, migrated: 0, failed: 0 };
  }

  let scanned = 0;
  let migrated = 0;
  let failed = 0;
  const next = { ...map };

  for (const [ownerId, photos] of Object.entries(map)) {
    if (!Array.isArray(photos) || photos.length === 0) continue;
    const updated = [];
    for (const p of photos) {
      scanned += 1;
      if (!p) {
        updated.push(p);
        continue;
      }
      if (isStorageUrl(p.url) || !isDataUrl(p.url)) {
        updated.push(p); // already migrated or non-image URL
        continue;
      }
      try {
        const m = await migrateOnePhoto({ photo: p, scope, ownerId });
        updated.push(m);
        if (m.url !== p.url) migrated += 1;
        else failed += 1;
      } catch (e) {
        failed += 1;
        updated.push(p); // keep legacy entry intact for retry
      }
    }
    next[ownerId] = updated;
  }

  if (migrated > 0) {
    try {
      await upsertSettingsByKey(key, next);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('[portalMigration] persist failed:', e?.message || e);
    }
  }
  return { scope, scanned, migrated, failed };
};

/**
 * Run the migration once per page-load. Silent. Returns a summary object.
 */
export const runPortalPhotoMigration = async ({ force = false } = {}) => {
  if (typeof sessionStorage !== 'undefined') {
    if (!force && sessionStorage.getItem(SESSION_FLAG)) {
      return { skipped: true };
    }
    sessionStorage.setItem(SESSION_FLAG, '1');
  }

  try {
    const [client, crew] = await Promise.all([
      migrateBucket({ key: CLIENT_KEY, scope: 'client' }),
      migrateBucket({ key: CREW_KEY, scope: 'crew' }),
    ]);
    const totalMigrated = client.migrated + crew.migrated;
    if (totalMigrated > 0 && typeof console !== 'undefined') {
      // Internal-only diagnostic; not surfaced in UI.
      // eslint-disable-next-line no-console
      console.info(
        `[portalMigration] migrated ${totalMigrated} legacy photo(s) to Storage ` +
          `(client: ${client.migrated}, crew: ${crew.migrated}).`
      );
    }
    return { skipped: false, client, crew };
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('[portalMigration] run failed:', e?.message || e);
    return { skipped: false, error: e?.message || 'migration_failed' };
  }
};
